import { Box, Stack, Typography } from '@mui/material'
import Container from '@mui/material/Container'
import { CreateCampaignHeader } from '~/features/campaign_create/Header'
import { CreateCampaignContext } from '~/contexts/CreateCampaignContext'
import { FC, useContext, useEffect, useState } from 'react'
import { NavigationButtons } from '../NavigationButtons'
import { useCampaignPageStepForm } from '../useCampaignPageStepForm'
import { FormProvider } from 'react-hook-form'
import { z } from 'zod'
import {
  SelectCostHandlingMethod,
  selectCostHandlingMethodSchemaFields,
} from '~/features/campaign_input/SelectCostHandlingMethod'
import {
  SelectExpenseEvidenceType,
  selectExpenseEvidenceTypeSchemaFields,
} from '~/features/campaign_input/SelectExpenseEvidenceType'
import { CampaignCategory, CampaignCostHandlingMethod, ExpenseEvidenceType } from '~/generated/graphql'
import { categoryField } from '~/features/campaign_input/type'
import { ExpenseInfoDialog } from '../ExpenseInfo/ExpenseInfoDialog'
import { LinkToInfo } from '~/components/common/LinkToInfo'
import { EXTERNAL_LINKS } from '~/lib/externalLinks'
import BurdenRateSelector, { influencerBurdenRateSchemaFields } from '~/features/campaign_input/BurdenRateSelector'
import { validateExpenseSetting, transformNoChangeCampaignIsNoExpense } from './validation'
import { CampaignPostFooter } from '~/components/CampaignPostFooter'

const schema = z.object({
  ...selectCostHandlingMethodSchemaFields,
  ...selectExpenseEvidenceTypeSchemaFields,
  ...categoryField,
  ...influencerBurdenRateSchemaFields,
})

export const ExpenseSetting: FC = () => {
  const { campaign, isExpenseCampaignAllowed, shouldOpenExpenseInfoDialog } = useContext(CreateCampaignContext)
  const { onSubmit, onBack, useFormMethods } = useCampaignPageStepForm({
    defaultValues: {
      costHandlingMethod: isExpenseCampaignAllowed
        ? campaign.costHandlingMethod
        : CampaignCostHandlingMethod.NoChargeCampaign,
      expenseEvidenceType: isExpenseCampaignAllowed
        ? campaign.expenseEvidenceType
        : ExpenseEvidenceType.NoEvidence,
      category: campaign.category,
      influencerBurdenRate: campaign.influencerBurdenRate,
    },
    schema: schema.superRefine(validateExpenseSetting).transform(transformNoChangeCampaignIsNoExpense),
  })

  const { watch } = useFormMethods
  const costHandlingMethod = watch('costHandlingMethod')

  // ダイアログの状態管理
  const [isDialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    if (shouldOpenExpenseInfoDialog()) {
      setDialogOpen(true)
    }
  }, [])

  return (
    <form onSubmit={onSubmit}>
      <CreateCampaignHeader title="利用する精算方法について教えてください" isNew={true} onBack={onBack} />
      <Container sx={{ display: 'flex', flexDirection: 'column' }} maxWidth="sm">
        <Stack gap={5}>
          <FormProvider {...useFormMethods}>
            <SelectCostHandlingMethod
              isExpenseCampaignAllowed={isExpenseCampaignAllowed}
              description={
                campaign.category === CampaignCategory.Service ? (
                  <CostHandlingMethodExpenseServiceDescription />
                ) : (
                  <CostHandlingMethodExpenseProductDescription />
                )
              }
              tooltip={
                <Stack gap={1}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    <Typography variant="body2">
                      ホテル等の宿泊施設のPRについては「インフルエンサー立替型」をご利用いただけません。
                    </Typography>
                    <Typography variant="body2">予めご了承ください。</Typography>
                  </Typography>
                  <Stack gap={0.5}>
                    <LinkToInfo textBefore="インフルエンサー立替型の詳細は" href={EXTERNAL_LINKS.EXPENSE_FAQ} />
                    <LinkToInfo textBefore="無償提供型の詳細は" href={EXTERNAL_LINKS.NO_CHARGE_FAQ} />
                  </Stack>
                </Stack>
              }
            />

            {costHandlingMethod === CampaignCostHandlingMethod.ExpenseCampaign && isExpenseCampaignAllowed && (
              <>
                <BurdenRateSelector tooltip={<BurdenRateSelectorTooltip />} />
                {campaign.category === CampaignCategory.Product && (
                  <SelectExpenseEvidenceType
                    tooltip={
                      <Stack gap={1}>
                        <ul style={{ margin: 0, paddingLeft: '16px' }}>
                          <li>
                            <Typography variant="body2">
                              精算時にインフルエンサーに立替の証憑を提出してもらいます。
                            </Typography>
                          </li>
                          <li>
                            <Typography variant="body2">
                              領収書の他、注文番号などの「注文を特定するために割り当てられる一意のID」の設定も可能です。
                            </Typography>
                          </li>
                        </ul>
                        <LinkToInfo href={EXTERNAL_LINKS.EXPENSE_AFTER_PUBLISH_FAQ} />
                      </Stack>
                    }
                  />
                )}
              </>
            )}
          </FormProvider>
        </Stack>

        <NavigationButtons />
      </Container>

      <ExpenseInfoDialog isOpen={isDialogOpen} setOpen={setDialogOpen} />
    </form>
  )
}

const CostHandlingMethodExpenseServiceDescription = () => {
  return (
    <Stack gap={1}>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
        インフルエンサー立替型は、会計時にインフルエンサーが費用を立替え、PR投稿完了後に店舗様がその金額を精算する仕組みです。
      </Typography>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
        インフルエンサーとの事前調整が不要となり、スムーズにオファーを進めることが可能です。
        また、予約が必要な場合でも一般のお客様と同じように予約が入るため、特別な対応は必要なく、より自然な形でのPRが期待できます。
      </Typography>
    </Stack>
  )
}
const CostHandlingMethodExpenseProductDescription = () => {
  return (
    <Stack gap={1}>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
        インフルエンサー立替型は、購入時にインフルエンサーが費用を立替え、PR投稿完了後に事業者様がその金額を精算する仕組みです。
      </Typography>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
        インフルエンサーへの発送連絡等が不要となり、スムーズにオファーを進めることが可能です。
        また、注文は一般のお客様と同じようにサイト経由で入るため、特別な対応は必要ありません。
      </Typography>
    </Stack>
  )
}

const BurdenRateSelectorTooltip: FC = () => {
  return (
    <>
      <Typography variant="body2">
        インフルエンサーの立替申請金額に対して、広告主様が負担する割合を設定します。
      </Typography>
      <Typography variant="body2" mt={1}>
        例えば還元率80%の場合、インフルエンサーが実際に支払った金額の80%を広告主様が負担し、残りの20%はインフルエンサーが負担します。
      </Typography>

      <Box mt={2}>
        <Typography variant="body2" fontWeight="bold">
          計算例（還元率80%の場合）
        </Typography>
        <CampaignPostFooter>
          <Stack gap={1}>
            <Typography variant="body2">インフルエンサーの立替申請額：5,000円</Typography>
            <Typography variant="body2">→ 広告主様のご負担額：5,000円 × 80% = 4,000円</Typography>
            <Typography variant="body2">→ インフルエンサー負担額：5,000円 × 20% = 1,000円</Typography>
          </Stack>
        </CampaignPostFooter>
      </Box>
    </>
  )
}
